/* Quantity enhancement only. Palo Alto owns variants, inventory, forms, cart and checkout. */
(() => {
  'use strict';
  if (customElements.get('halohush-main-bundle')) return;

  class HaloHushMainBundle extends HTMLElement {
    static pricing({unit = 0, compareAt = 0, currency, configured, shipping, discount, discountMode, discountPercent = 0, currencyStep = 1, oneTime, quantity, available}) {
      const percentageMode = discountMode === 'percentage';
      const bundleEnabled = configured && (percentageMode || currency === 'USD') && oneTime;
      const percent = Math.min(100, Math.max(0, Number(discountPercent) || 0));
      const step = Number(currencyStep) === 100 ? 100 : 1;
      const saving = !bundleEnabled ? 0 : percentageMode
        ? Math.min(unit * 2, Math.round((unit * 2 * percent / 100) / step) * step)
        : Math.min(Math.max(0, discount || 0), unit);
      const pair = unit * 2 - saving;
      const nativeCompare = Number(compareAt);
      const compareUnit = Number.isFinite(nativeCompare) && nativeCompare > unit ? nativeCompare : 0;
      const comparePair = compareUnit > 0 ? compareUnit * 2 : saving > 0 ? unit * 2 : 0;
      return {currency, unit, pair, compareUnit, comparePair, extra: unit - saving, saving, per: pair / 2, quantity, available,
        oneTime, bundleEnabled, shippingEnabled: shipping && currency === 'USD' && oneTime};
    }

    connectedCallback() {
      customElements.whenDefined('product-info').then(() => {
        if (this.isConnected && !this.started) this.start();
      });
    }

    start() {
      this.info = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.info || !this.form) return;
      this.quantityInput = Array.from(this.info.querySelectorAll('[name="quantity"]'))
        .find(input => input.form === this.form) || this.form.elements.namedItem('quantity');
      if (!this.quantityInput) return;
      this.started = true;
      this.section = this.closest('[data-halohush-native]');
      this.section?.classList.add('hhm-ready');
      this.addButton = this.form.querySelector('[data-add-to-cart]');
      this.stock = JSON.parse(this.querySelector('[data-hhm-variant-stock]')?.textContent || '{}');
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.refresh());
      };
      // Keep the original gallery/navigation instances and listeners; only align compact cells to the start.
      this.alignGallery = () => requestAnimationFrame(() => {
        const nav = this.info.flktyNav;
        if (nav && nav.options.cellAlign !== 'left') { nav.options.cellAlign = 'left'; nav.resize(); }
      });
      this.alignGallery();
      document.addEventListener('theme:resize:width', this.alignGallery, options);
      this.info.addEventListener('theme:variant:prices-updated', this.schedule, options);
      this.info.addEventListener('input', this.schedule, options);
      this.info.addEventListener('change', this.schedule, options);
      document.addEventListener('theme:cart:refresh', this.schedule, options);
      document.addEventListener('theme:cart:close', this.schedule, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-hhm-quantity]');
        if (button && this.contains(button) && !button.disabled) this.select(Number(button.dataset.hhmQuantity));
      }, options);
      this.addEventListener('keydown', event => {
        if (!event.target.closest('[data-hhm-quantity]') || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const quantity = Number(this.quantityInput.value) === 2 ? 1 : 2;
        if (this.select(quantity)) this.querySelector(`[data-hhm-quantity="${quantity}"]`)?.focus();
      }, options);
      this.buttonObserver = new MutationObserver(this.schedule);
      if (this.addButton) this.buttonObserver.observe(this.addButton, {attributes: true, attributeFilter: ['disabled']});
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.quantityInput, {attributes: true,
        attributeFilter: ['min', 'max', 'step', 'data-cart-quantity', 'data-min', 'data-max']});
      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.quantityUpdate) {
        this.unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.quantityUpdate, this.schedule);
      }
      this.refresh();
      if (this.dataset.defaultQuantity === '2' && this.state.bundleEnabled) this.select(2);
    }

    readVariant() {
      try { return JSON.parse(this.info.querySelector('[data-selected-variant]')?.textContent || 'null'); }
      catch (_) { return null; }
    }

    isOneTime() {
      return this.dataset.requiresSellingPlan !== 'true' &&
        !new FormData(this.form).get('selling_plan') &&
        !this.form.querySelector('[data-add-to-cart]')?.dataset.sellingPlanId;
    }

    allowed(quantity, variant = this.variant) {
      if (!variant?.available || !this.isOneTime()) return false;
      const input = this.quantityInput;
      const min = Number(input.min) || 1;
      const step = Number(input.step) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      if (quantity < min || quantity > max || (quantity - min) % step !== 0) return false;
      const stock = this.stock[String(variant.id)];
      const cartQuantity = Number(input.dataset.cartQuantity) || 0;
      return !(stock?.managed && stock.policy === 'deny' && quantity + cartQuantity > stock.quantity);
    }

    money(cents) {
      if (Number(this.dataset.currencyStep) === 100) return new Intl.NumberFormat(document.documentElement.lang || 'es-US', {
        style: 'currency', currency: this.dataset.currency, maximumFractionDigits: 0
      }).format(cents / 100);
      // Whole USD amounts match the approved $34 / $39 display; checkout still uses Shopify amounts.
      if (this.dataset.currency === 'USD') return new Intl.NumberFormat('es-US', {
        style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2
      }).format(cents / 100);
      // Other markets retain the native store currency format.
      if (window.theme?.formatMoney && window.theme?.moneyFormat) {
        const template = document.createElement('template');
        template.innerHTML = window.theme.formatMoney(cents, window.theme.moneyFormat);
        return template.content.textContent;
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'es-US', {
        style: 'currency', currencyDisplay: 'narrowSymbol', currency: this.dataset.currency || 'USD'
      }).format(cents / 100);
    }

    refresh() {
      if (!this.isConnected || !this.started) return;
      this.variant = this.readVariant();
      const quantity = Number(this.quantityInput.value) || 1;
      this.state = HaloHushMainBundle.pricing({
        unit: this.variant?.price || 0,
        compareAt: this.variant?.compare_at_price || 0,
        currency: this.dataset.currency,
        configured: this.dataset.bundleConfigured === 'true',
        shipping: this.dataset.shippingConfigured === 'true',
        discount: Number(this.dataset.discount),
        discountMode: this.dataset.discountMode,
        discountPercent: Number(this.dataset.discountPercent),
        currencyStep: Number(this.dataset.currencyStep),
        oneTime: this.isOneTime(), quantity, available: Boolean(this.variant?.available)
      });
      for (const node of this.info.querySelectorAll('[data-hhm-money]')) {
        const cents = node.dataset.hhmMoney === 'regular' ? this.state.unit * 2 : this.state[node.dataset.hhmMoney];
        node.textContent = this.variant ? this.money(cents) : '—';
        if (node.dataset.hhmMoney === 'regular') node.hidden = !this.state.bundleEnabled;
        if (node.dataset.hhmMoney === 'compareUnit' || node.dataset.hhmMoney === 'comparePair') {
          const current = node.dataset.hhmMoney === 'compareUnit' ? this.state.unit : this.state.pair;
          node.hidden = !this.variant || !(cents > current);
        }
      }
      for (const node of this.info.querySelectorAll('[data-hhm-discount]')) node.hidden = !this.state.bundleEnabled;
      for (const node of this.info.querySelectorAll('[data-hhm-shipping]')) node.hidden = !this.state.shippingEnabled;
      for (const node of this.info.querySelectorAll('[data-hhm-shipping-fallback]')) node.hidden = this.state.shippingEnabled;
      for (const node of this.info.querySelectorAll('[data-hhm-selected-shipping]')) {
        node.textContent = this.state.shippingEnabled && quantity >= 2 ? node.dataset.freeLabel : node.dataset.paidLabel;
      }
      for (const button of this.querySelectorAll('[data-hhm-quantity]')) {
        const count = Number(button.dataset.hhmQuantity);
        button.disabled = !this.allowed(count);
        button.setAttribute('aria-checked', String(count === quantity));
        button.tabIndex = count === quantity ? 0 : -1;
      }
      if (![1, 2].includes(quantity) || !this.allowed(quantity)) {
        const first = this.querySelector('[data-hhm-quantity]:not(:disabled)');
        if (first) first.tabIndex = 0;
      }
      const formWrapper = this.form.closest('[data-hhm-show-quantity]');
      // Preserve a usable native quantity control for plans and nonstandard quantity rules.
      formWrapper?.classList.toggle('hhm-quantity-required', !this.state.oneTime || (!this.allowed(1) && !this.allowed(2) && this.state.available));
      this.updateBuyButton();
      const status = this.querySelector('[data-hhm-status]');
      if (!this.state.available) status.textContent = 'La variante seleccionada no está disponible.';
      else if (!this.state.oneTime) status.textContent = 'Los paquetes corresponden a la compra única. Usa las opciones de tu plan para cambiar la cantidad.';
      else if (![1, 2].includes(quantity)) status.textContent = `Cantidad seleccionada en el formulario: ${quantity}. El carrito mostrará los descuentos aplicables.`;
      else status.textContent = `${quantity === 1 ? '1 botella seleccionada' : '2 botellas seleccionadas'}.`;
      document.dispatchEvent(new CustomEvent('halohush:pricing', {detail: {
        sectionId: this.dataset.sectionId, productId: this.dataset.productId,
        variantId: this.variant?.id || null, ...this.state
      }}));
    }

    selectedTotal() {
      return this.state.unit * this.state.quantity - this.state.saving * Math.floor(this.state.quantity / 2);
    }

    updateBuyButton() {
      const preorder = Boolean(this.form.querySelector('[id^="PreorderProperty-"] input')) || this.section?.hasAttribute('data-preorder-type-product');
      const active = Boolean(this.variant && this.state.available && this.state.oneTime && !preorder);
      const total = this.money(this.selectedTotal());
      const mainLabel = this.state.quantity === 1 ? this.dataset.ctaSingle : this.dataset.ctaMultiple;
      const quantityLabel = this.state.quantity === 1 ? this.dataset.singleLabel : this.dataset.multipleLabel;
      const sticky = this.info.querySelector('cart-bar');
      const buttons = [this.addButton, sticky?.querySelector('[data-cart-bar-add-to-cart]')].filter(Boolean);
      for (const button of buttons) {
        const isSticky = button !== this.addButton;
        let copy = button.querySelector('[data-hhm-button-copy]');
        if (!copy) {
          copy = document.createElement('span');
          copy.dataset.hhmButtonCopy = '';
          const label = document.createElement('span'); label.dataset.hhmButtonLabel = '';
          copy.append(label);
          copy.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" class="hhm-icon" aria-hidden="true"><path d="M4 12h16m-6-6 6 6-6 6"/></svg>');
          button.append(copy);
        }
        const text = `${(isSticky ? (this.dataset.ctaSticky || 'AGREGAR') : (mainLabel || 'AGREGAR AL CARRITO')).replace('{quantity}', String(this.state.quantity))} ${isSticky ? '·' : '—'} ${total}`;
        const label = copy.querySelector('[data-hhm-button-label]');
        if (label.textContent !== text) label.textContent = text;
        button.toggleAttribute('data-hhm-cta-active', active);
      }
      if (sticky) {
        const info = sticky.querySelector('.cart-bar__info');
        let summary = sticky.querySelector('[data-hhm-sticky-copy]');
        if (!summary && info) {
          summary = document.createElement('div'); summary.dataset.hhmStickyCopy = '';
          summary.innerHTML = '<strong data-hhm-sticky-title></strong><small data-hhm-sticky-shipping></small>';
          info.append(summary);
        }
        sticky.classList.toggle('hhm-sticky-ready', active);
        if (summary) {
          summary.querySelector('[data-hhm-sticky-title]').textContent = `${(quantityLabel || '{quantity} botellas').replace('{quantity}', String(this.state.quantity))} · ${total}`;
          const shipping = this.info.querySelector('[data-hhm-selected-shipping]');
          summary.querySelector('[data-hhm-sticky-shipping]').textContent = shipping?.textContent || '';
        }
      }
    }

    select(quantity) {
      if (!this.started || !this.allowed(quantity)) return false;
      this.quantityInput.value = String(quantity);
      this.quantityInput.dispatchEvent(new Event('input', {bubbles: true}));
      this.quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
      const label = this.quantityInput.closest('[data-quantity-wrapper]')?.querySelector('[data-quantity-select]');
      if (label) label.textContent = String(quantity);
      this.refresh();
      return true;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.buttonObserver?.disconnect();
      this.section?.classList.remove('hhm-ready');
      this.unsubscribe?.();
      cancelAnimationFrame(this.frame);
      this.started = false;
    }
  }

  customElements.define('halohush-main-bundle', HaloHushMainBundle);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-hh-select-pair]');
    if (!trigger) return;
    const blocks = Array.from(document.querySelectorAll('halohush-main-bundle'));
    const bundle = blocks.find(node => !trigger.dataset.productId || node.dataset.productId === trigger.dataset.productId);
    if (!bundle) return;
    event.preventDefault();
    bundle.select(2);
    bundle.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
    const target = bundle.querySelector('[data-hhm-quantity="2"]:not(:disabled)') || bundle.info?.querySelector('[data-add-to-cart]');
    target?.focus({preventScroll: true});
  });
})();
